import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { BellRing, Building2, KeyRound, Languages, LogOut, Monitor, Moon, Radio, Sun, SunMoon } from 'lucide-react';
import { logout, refreshSession } from '@/lib/api/auth';
import { isLocale, LOCALES, msg, setLocale, t, useLocale } from '@/lib/i18n';
import { meQuery, useMe } from '@/lib/me';
import { setThemePref, type ThemePref, useThemePref } from '@/lib/theme';
import { Avatar, Menu, MenuItem, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuSeparator, MenuSub, toast } from '@/ui';
import { savePreference } from './preferences';
import { useRealtimeState } from './realtime';
import s from './TopBar.module.css';

const STATUS_LABEL = {
  idle: msg('Live updates: off'),
  connecting: msg('Live updates: connecting'),
  open: msg('Live updates: on'),
  reconnecting: msg('Live updates: reconnecting'),
  offline: msg('Live updates: offline'),
} as const;

function RealtimeStatus() {
  const status = useRealtimeState();
  return (
    <MenuItem icon={Radio} disabled>
      <span className={s.liveRow}>
        <span className={status === 'open' ? s.liveOn : s.liveOff} aria-hidden />
        {t(STATUS_LABEL[status])}
      </span>
    </MenuItem>
  );
}

export function UserMenu() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const locale = useLocale();
  const theme = useThemePref();

  const switchCompany = async (id: string) => {
    if (id === me.company?.id) return;
    const ok = await refreshSession(id);
    if (!ok) {
      toast({ title: t('Could not switch company'), tone: 'danger' });
      return;
    }
    qc.removeQueries();
    await qc.fetchQuery(meQuery);
    await navigate({ to: '/', search: {} });
  };

  const signOut = async () => {
    await logout();
    qc.clear();
    await navigate({ to: '/login', search: {} });
  };

  return (
    <Menu
      label={t('Account')}
      trigger={
        <button type="button" className={s.avatarButton} aria-label={t('Account: {name}', { name: me.user.name })}>
          <Avatar name={me.user.name} size="sm" />
        </button>
      }
    >
      <div className={s.who}>
        <span className={s.whoName}>{me.user.name}</span>
        <span className={s.whoMeta}>{me.user.email}</span>
        <span className={s.whoMeta}>
          {me.persona.label} · {me.company?.name}
        </span>
      </div>
      <MenuSeparator />
      <MenuItem icon={BellRing} onSelect={() => void navigate({ to: '/me/notifications' })}>
        {t('Notifications')}
      </MenuItem>
      <MenuItem icon={KeyRound} onSelect={() => void navigate({ to: '/me/password' })}>
        {t('Change password')}
      </MenuItem>
      {me.companies.length > 1 && (
        <MenuSub icon={Building2} label={t('Switch company')}>
          <MenuLabel>{t('Companies')}</MenuLabel>
          <MenuRadioGroup value={me.company?.id ?? ''} onChange={(v) => void switchCompany(v)}>
            {me.companies.map((c) => (
              <MenuRadioItem key={c.id} value={c.id}>
                {c.name}
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
        </MenuSub>
      )}
      <MenuSub icon={Languages} label={t('Language')}>
        <MenuRadioGroup
          value={locale}
          onChange={(v) => {
            if (!isLocale(v)) return;
            setLocale(v);
            savePreference({ locale: v });
          }}
        >
          {LOCALES.map((l) => (
            <MenuRadioItem key={l.value} value={l.value}>
              {l.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuSub>
      <MenuSub icon={SunMoon} label={t('Theme')}>
        <MenuRadioGroup
          value={theme}
          onChange={(v) => {
            setThemePref(v as ThemePref);
            savePreference({ theme: v });
          }}
        >
          <MenuRadioItem value="system" icon={Monitor}>
            {t('Same as device')}
          </MenuRadioItem>
          <MenuRadioItem value="light" icon={Sun}>
            {t('Light')}
          </MenuRadioItem>
          <MenuRadioItem value="dark" icon={Moon}>
            {t('Dark')}
          </MenuRadioItem>
        </MenuRadioGroup>
      </MenuSub>
      <RealtimeStatus />
      <MenuSeparator />
      <MenuItem icon={LogOut} onSelect={() => void signOut()}>
        {t('Sign out')}
      </MenuItem>
    </Menu>
  );
}
